import React from 'react';
import './PlaceSearchResults.css';
import Location from '@/models/location';
import PlaceSearchResultCard from './PlaceSearchResultCard';

interface PlaceSearchResultsProps {
    results: Location[];
    /** Index is stable per render for multi-select keys */
    onPlaceSelect: (location: Location, index: number) => void;
    onPlacePreview?: (location: Location) => void;
    isAdding?: boolean;
    focusedIndex?: number;
    /** Multi-select (Explore + list batch add): + toggles selection */
    selectable?: boolean;
    selectedKeys?: Set<string>;
    /** Defaults to _id / google_place_id / index-based id */
    getRowKey?: (location: Location, index: number) => string;
    /** Omit outer margin when nested inside a scroll panel (e.g. batch list section) */
    embedded?: boolean;
}

const defaultRowKey = (location: Location, index: number): string =>
    String(location._id || location.google_place_id || `row-${index}`);

const PlaceSearchResults: React.FC<PlaceSearchResultsProps> = ({
    results,
    onPlaceSelect,
    onPlacePreview,
    isAdding = false,
    focusedIndex = -1,
    selectable = false,
    selectedKeys,
    getRowKey = defaultRowKey,
    embedded = false,
}) => {
    if (results.length === 0) {
        return null;
    }

    return (
        <div
            className={`place-search-results${embedded ? ' place-search-results--embedded' : ''}`}
        >
            <div className="place-search-results-list" role="list">
                {results.map((location, index) => {
                    const locationKey = getRowKey(location, index);
                    const isSelected = Boolean(selectable && selectedKeys?.has(locationKey));

                    return (
                        <PlaceSearchResultCard
                            key={locationKey}
                            location={location}
                            onAdd={() => onPlaceSelect(location, index)}
                            onPreview={onPlacePreview}
                            isAdding={isAdding}
                            isFocused={focusedIndex === index}
                            selectable={selectable}
                            isSelected={isSelected}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default React.memo(PlaceSearchResults);
